import React, { useState } from 'react'
import { motion } from 'motion/react'
import { Plus } from 'lucide-react'
import { Feature, Project } from '../../api/client'
import SummaryConfigEditor, { EditorSurface } from './SummaryConfigEditor'

/**
 * AddSummaryButton — the slim "Summaries" header with a "+ Add summary" action
 * that opens the shared SummaryConfigEditor in create mode. Replaces the
 * standalone SummaryConfigList's add affordance; per-config edit / delete now
 * lives on each card's gear (see SummaryConfigMenu). variant="tile" renders a
 * dashed ghost tile meant to sit as the last item of the cards grid, for
 * hosts (the dashboard) where the section has no header and the first card
 * should sit as high as possible — end placement costs nothing at the top,
 * while the tile stays inviting enough to keep experimentation alive.
 *
 * Motion vocab (CLAUDE.md): the add button uses whileHover scale 1.1 /
 * whileTap scale 0.9.
 */

const BUTTON_SPRING = { type: 'spring', stiffness: 400, damping: 20 } as const

interface AddSummaryButtonProps {
  surface: EditorSurface
  featureId?: number
  projectId?: number
  /** Sibling features for the editor's source / stratification pickers. */
  features?: Feature[]
  /** Selectable projects for the dashboard surface's project-tag picker. */
  projects?: Project[]
  /** Notified after a successful create so the host can refresh its cards. */
  onChanged?: () => void
  /** 'header': the labeled "Summaries" row. 'tile': a dashed add tile. */
  variant?: 'header' | 'tile'
}

const AddSummaryButton: React.FC<AddSummaryButtonProps> = ({
  surface,
  featureId,
  projectId,
  features = [],
  projects = [],
  onChanged,
  variant = 'header',
}) => {
  const [editorOpen, setEditorOpen] = useState(false)

  const editor = (
    <SummaryConfigEditor
      isOpen={editorOpen}
      onClose={() => setEditorOpen(false)}
      onSaved={() => onChanged?.()}
      surface={surface}
      featureId={featureId}
      projectId={projectId}
      features={features}
      projects={projects}
      existing={null}
    />
  )

  if (variant === 'tile') {
    return (
      <>
        <motion.button
          type="button"
          onClick={() => setEditorOpen(true)}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          transition={BUTTON_SPRING}
          className="flex min-h-[72px] w-full self-start items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-gray-200 px-4 py-6 text-sm font-medium text-gray-400 transition-colors hover:border-amber-300 hover:bg-amber-50/50 hover:text-amber-900"
          data-testid="add-summary-button"
        >
          <Plus size={18} strokeWidth={2.5} /> Add summary
        </motion.button>
        {editor}
      </>
    )
  }

  return (
    <div className="mb-3 flex items-center justify-between">
      <h3 className="text-base font-semibold text-gray-900">Summaries</h3>
      <motion.button
        type="button"
        onClick={() => setEditorOpen(true)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        transition={BUTTON_SPRING}
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-amber-800 transition-colors hover:bg-amber-50 hover:text-amber-900"
        data-testid="add-summary-button"
      >
        <Plus size={16} strokeWidth={2.5} /> Add summary
      </motion.button>

      {editor}
    </div>
  )
}

export default AddSummaryButton
